import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

import { API } from '../config';
const o = { withCredentials: true };

const History = () => {
  const [tab, setTab] = useState('bots');
  const [botTrades, setBotTrades] = useState([]);
  const [botEquity, setBotEquity] = useState([]);
  const [manualEquity, setManualEquity] = useState([]);
  const [arbEquity, setArbEquity] = useState([]);
  const [prices, setPrices] = useState({});
  const [manualTrades, setManualTrades] = useState([]);
  const [arbTrades, setArbTrades] = useState([]);
  const [arbTotal, setArbTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const openTradesRef = useRef([]);

  const fetchPrices = async (openTrades) => {
    if (!openTrades.length) return;
    // Combinaciones únicas exchange:symbol para no repetir llamadas
    const seen = new Set();
    const combos = [];
    for (const t of openTrades) {
      const ex = t.exchange_id || 'bitunix';
      const sym = t.symbol || 'BTC/USDT';
      const key = `${ex}:${sym}`;
      if (!seen.has(key)) { seen.add(key); combos.push({ ex, sym, key }); }
    }
    // Todas las llamadas en paralelo con timeout de 8 segundos
    const results = await Promise.all(combos.map(async ({ ex, sym, key }) => {
      try {
        const r = await axios.get(`${API}/market/ticker/${ex}`,
          { params: { symbol: sym }, ...o, timeout: 8000 });
        return r.data?.last ? { key, price: r.data.last } : null;
      } catch { return null; }
    }));
    const p = {};
    results.forEach(r => { if (r) p[r.key] = r.price; });
    setPrices(p);
  };

  const load = async () => {
    try {
      const [bt, mt, st, arb, eq, pf] = await Promise.all([
        // Sin el límite de 200: con mucha actividad en el día, esa ventana
        // dejaba fuera operaciones más viejas y la tabla mostraba una
        // porción sesgada del historial real (mismo bug ya corregido en
        // la curva de equity de más abajo).
        axios.get(`${API}/aegis/trades?limit=5000`, o).catch(() => ({ data: { trades: [] } })),
        axios.get(`${API}/trades?limit=200`, o).catch(() => ({ data: { trades: [] } })),
        axios.get(`${API}/aegis/stats`, o).catch(() => ({ data: null })),
        axios.get(`${API}/arbitrage/historial?limit=200`, o).catch(() => ({ data: { historial: [] } })),
        axios.get(`${API}/aegis/equity-curve`, o).catch(() => ({ data: { puntos: [] } })),
        axios.get(`${API}/portfolio/trades-full`, o).catch(() => ({ data: { trades: [] } })),
      ]);
      setBotEquity(eq.data.puntos || []);
      // Curvas de equity de Manual y Arbitraje sin el límite de 200 que sí
      // aplica a las tablas de abajo (mismo bug que se corrigió en Bots/IA).
      const _buildEq = (rows) => {
        let pAcc = 0, rAcc = 0;
        return rows.map((t, i) => {
          if (t.paper_trading) pAcc = +(pAcc + t.pnl).toFixed(4);
          else rAcc = +(rAcc + t.pnl).toFixed(4);
          return {
            i, fecha: (t.ts || '').slice(5, 13).replace('T', ' '),
            paper: t.paper_trading ? pAcc : undefined,
            real: !t.paper_trading ? rAcc : undefined,
          };
        });
      };
      const _portfolioRows = pf.data.trades || [];
      setManualEquity(_buildEq(_portfolioRows.filter(t => t.fuente === 'Trading Manual' || t.fuente === 'Copy Trading')));
      setArbEquity(_buildEq(_portfolioRows.filter(t => t.fuente === 'Arbitraje')));
      // Al cerrar una posición, el sistema guarda el cierre como un trade NUEVO
      // (con el P&L real) y deja el de apertura marcado closed=true pero sin su
      // propio profit_loss_usd — esa fila de apertura huérfana no aporta nada
      // (el resultado ya está en la fila de cierre), así que no se muestra.
      // Las posiciones ABIERTAS van primero (arriba), y dentro de cada grupo
      // (abiertas / cerradas) más reciente primero -- así lo que necesita
      // atención ahora mismo no queda enterrado entre cientos de cerradas.
      const _sortOpenFirst = (rows) => rows.slice().sort((a, b) => {
        const aOpen = !a.closed && a.profit_loss_usd == null;
        const bOpen = !b.closed && b.profit_loss_usd == null;
        if (aOpen !== bOpen) return aOpen ? -1 : 1;
        const ta = a.timestamp || a.created_at || '';
        const tb = b.timestamp || b.created_at || '';
        return tb.localeCompare(ta);
      });
      const bots = _sortOpenFirst((bt.data.trades || []).filter(t => !(t.closed && t.profit_loss_usd == null)));
      const manual = _sortOpenFirst(mt.data.trades || []);
      setBotTrades(bots);
      setManualTrades(manual);
      setArbTrades(arb.data.historial || []);
      setArbTotal(arb.data.total ?? (arb.data.historial || []).length);
      setStats(st.data);
      // Mostrar la página inmediatamente — los precios llegan en paralelo después
      setLoading(false);
      const open = [...bots, ...manual].filter(t => !t.closed && t.profit_loss_usd == null);
      openTradesRef.current = open;
      fetchPrices(open); // sin await — se actualiza en segundo plano
    } catch { setLoading(false); }
  };

  useEffect(() => {
    load();
    const fullId = setInterval(load, 15000);
    // Refresco de precios cada 3s para P&L en tiempo real (solo posiciones abiertas)
    const priceId = setInterval(() => fetchPrices(openTradesRef.current), 3000);
    return () => { clearInterval(fullId); clearInterval(priceId); };
  }, []);

  const getUnrealizedPnl = (t) => {
    if (t.closed || t.profit_loss_usd != null) return null;
    const ex = t.exchange_id || 'bitunix';
    const sym = t.symbol || 'BTC/USDT';
    const currentPrice = prices[`${ex}:${sym}`];
    if (!currentPrice || !t.price) return null;
    const qty = t.amount || 0;
    const leverage = t.leverage || 1;
    const isLong = t.action === 'buy' || t.position === 'long';
    const delta = isLong ? (currentPrice - t.price) : (t.price - currentPrice);
    const pnl = delta * qty * leverage;
    const feePct = t.market_type === 'futures' ? 0.0006 : 0.002;
    const fees = (t.price + currentPrice) * qty * feePct;
    return { pnl: pnl - fees, currentPrice, fees };
  };

  const closeTrade = async (trade) => {
    if (!window.confirm('¿Cerrar esta posición al precio actual?')) return;
    try {
      // Trade de bot/IA (aegis_trades) — tienen campo 'action'; manuales tienen 'side'
      if (trade.action) {
        const r = await axios.post(`${API}/aegis/trades/${trade.trade_id}/close`, {}, o);
        toast.success(`Cerrada: P&L $${r.data.pnl?.toFixed(2) || '0'}`);
      } else {
        // Trade manual (trades) — usar endpoint de cierre manual
        const r = await axios.post(`${API}/trades/${trade.trade_id}/close`, {}, o);
        toast.success(`Cerrada: P&L $${r.data.pnl?.toFixed(2) || '0'}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cerrar'); }
  };

  const deleteBotHistory = async (botId) => {
    if (!window.confirm('¿Eliminar todo el historial de este bot eliminado? Esta acción no se puede deshacer.')) return;
    try {
      const r = await axios.delete(`${API}/aegis/trades/bot/${botId}`, o);
      toast.success(`${r.data.deleted_count} operaciones eliminadas del historial`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar historial'); }
  };

  const tabs = [
    { id: 'bots', label: `Bots / IA (${botTrades.length})` },
    { id: 'manual', label: `Trading Manual (${manualTrades.length})` },
    { id: 'arbitrage', label: `Arbitraje (${arbTotal})` },
  ];

  const formatDate = (ts) => {
    if (!ts) return '-';
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  const formatPrice = (p) => {
    if (p == null) return '-';
    const n = Number(p);
    return `$${n < 1 ? n.toFixed(8) : n.toFixed(2)}`;
  };

  // Ganancia/pérdida real con monedas de precio muy chico (PEPE, SUPRA, etc.)
  // -- antes cualquier P&L menor a 1 centavo se mostraba como "$0.00" fijo
  // (a veces con etiqueta "COMISIÓN"), tapando una ganancia o pérdida real
  // aunque fuera chica. Ahora, para esos casos, se muestran los decimales
  // que hagan falta (hasta 8) en vez de redondear a cero -- igual en verde
  // (ganancia) que en rojo (pérdida).
  const formatPnl = (v) => {
    const n = Number(v);
    if (!n) return '0.00';
    const abs = Math.abs(n);
    if (abs < 0.01) {
      let s = n.toFixed(8).replace(/0+$/, '');
      if (s.endsWith('.')) s += '00';
      return s;
    }
    return n.toFixed(2);
  };

  const EquityChart = ({ data }) => {
    if (data.length < 1) return null;
    const hasPaper = data.some(d => d.paper !== undefined);
    const hasReal  = data.some(d => d.real  !== undefined);
    const lastPaper = hasPaper ? [...data].reverse().find(d => d.paper !== undefined)?.paper ?? 0 : null;
    const lastReal  = hasReal  ? [...data].reverse().find(d => d.real  !== undefined)?.real  ?? 0 : null;
    return (
      <div className="mb-4 p-3 bg-[#0D0D0D] rounded border border-[#27272A]">
        <div className="flex items-center gap-4 mb-2 text-xs">
          <span className="text-[#A1A1AA] font-medium">Curva de Equity</span>
          {hasPaper && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-[#00D4FF]" />
              <span className="text-[#A1A1AA]">Paper:</span>
              <span className={`font-bold mono ${lastPaper >= 0 ? 'text-[#00D4FF]' : 'text-red-400'}`}>
                {lastPaper >= 0 ? '+' : ''}${lastPaper.toFixed(2)}
              </span>
            </span>
          )}
          {hasReal && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-[#FFD600]" />
              <span className="text-[#A1A1AA]">Real:</span>
              <span className={`font-bold mono ${lastReal >= 0 ? 'text-[#FFD600]' : 'text-red-400'}`}>
                {lastReal >= 0 ? '+' : ''}${lastReal.toFixed(2)}
              </span>
            </span>
          )}
        </div>
        <ResponsiveContainer width="100%" height={120}>
          <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <XAxis dataKey="fecha" tick={{ fill: '#52525B', fontSize: 10 }} />
            <YAxis tick={{ fill: '#52525B', fontSize: 10 }} width={50} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid #27272A', borderRadius: 4, fontSize: 11 }}
              formatter={(v, n) => v != null ? [`$${Number(v).toFixed(4)}`, n === 'paper' ? 'Paper' : 'Real'] : null}
            />
            {hasPaper && <Line type="monotone" dataKey="paper" stroke="#00D4FF" strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls />}
            {hasReal  && <Line type="monotone" dataKey="real"  stroke="#FFD600" strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls />}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  if (loading) return (
    <Layout><div className="flex items-center justify-center h-full">
      <div className="text-center"><div className="aegis-spinner mx-auto"></div><p className="text-[#A1A1AA] text-sm mt-3">Cargando...</p></div>
    </div></Layout>
  );

  return (
    <Layout>
      <div className="space-y-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Historial de Operaciones</h1>
          <p className="text-[#A1A1AA] mt-1">Todas tus operaciones: bots, IA, trading manual y copy trading</p>
        </div>

        {/* Estadísticas rápidas */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-[#121212] border border-[#27272A] rounded p-3 text-center">
              <p className="text-xs text-[#A1A1AA]">Total Operaciones</p>
              <p className="text-xl font-bold text-white mono">{(botTrades.length + manualTrades.length)}</p>
            </div>
            <div className="bg-[#121212] border border-[#27272A] rounded p-3 text-center">
              <p className="text-xs text-[#A1A1AA]">P&L Bots/IA</p>
              <p className={`text-xl font-bold mono ${(stats.total_pnl || 0) >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                ${(stats.total_pnl || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-[#121212] border border-[#27272A] rounded p-3 text-center">
              <p className="text-xs text-[#A1A1AA]">Win Rate</p>
              <p className={`text-xl font-bold mono ${(stats.win_rate || 0) >= 50 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                {(stats.win_rate || 0).toFixed(1)}%
              </p>
            </div>
            <div className="bg-[#121212] border border-[#27272A] rounded p-3 text-center">
              <p className="text-xs text-[#A1A1AA]">Trades Ganadores</p>
              <p className="text-xl font-bold text-white mono">{stats.winning_trades || 0}/{stats.total_trades || 0}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-[#1E1E1E] p-1 rounded">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded text-sm font-medium transition-all ${tab === t.id ? 'bg-[#00D4FF] text-black' : 'text-[#A1A1AA] hover:text-white hover:bg-[#27272A]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Bots / IA */}
        {tab === 'bots' && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white text-base">Operaciones de Bots e IA</CardTitle></CardHeader>
            <CardContent>
              <EquityChart data={botEquity} />
              {botTrades.length === 0 ? (
                <p className="text-[#A1A1AA] text-center py-6">Sin operaciones de bots todavia</p>
              ) : (
                <div style={{maxHeight:'420px',overflowY:'auto',overflowX:'auto'}}
                     className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-[#27272A]">
                  <table className="w-full text-sm min-w-[750px]">
                    <thead className="sticky top-0 bg-[#121212] z-10">
                      <tr className="border-b border-[#27272A] text-[#A1A1AA] text-xs">
                        <th className="text-left p-2">Fecha</th>
                        <th className="text-left p-2">Exchange</th>
                        <th className="text-left p-2">Par</th>
                        <th className="text-left p-2">Accion</th>
                        <th className="text-right p-2">P. Apertura</th>
                        <th className="text-right p-2">P. Cierre/Actual</th>
                        <th className="text-right p-2">Cantidad</th>
                        <th className="text-right p-2">P&L</th>
                        <th className="text-left p-2">Modo</th>
                        <th className="text-left p-2">Accion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {botTrades.map((t, i) => {
                        const unrealized = getUnrealizedPnl(t);
                        const isOpen = !t.closed && t.profit_loss_usd == null;
                        // Si la fila cierra una posicion (tiene entry_price), mostrar la
                        // accion que la ABRIO (la contraria a t.action, que es la que cierra).
                        const tieneParEntrada = t.profit_loss_usd != null && t.entry_price != null;
                        const accionMostrada = tieneParEntrada
                          ? (t.action === 'buy' ? 'sell' : 'buy')
                          : (t.action || t.side);
                        return (
                        <tr key={t.trade_id || i} className={`border-b border-[#27272A]/30 hover:bg-[#1E1E1E] ${isOpen ? 'bg-[#1E1E1E]/50' : ''}`}>
                          <td className="p-2 text-[#A1A1AA] text-xs">{formatDate(t.timestamp)}</td>
                          <td className="p-2 text-[#A1A1AA] uppercase text-xs font-medium">{t.exchange_id || '-'}</td>
                          <td className="p-2 text-white mono">{t.symbol}</td>
                          <td className="p-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${accionMostrada === 'buy' ? 'bg-[#00FF88]/15 text-[#00FF88]' : accionMostrada === 'sell' ? 'bg-[#FF3B30]/15 text-[#FF3B30]' : 'bg-[#27272A] text-[#A1A1AA]'}`}>
                              {accionMostrada?.toUpperCase() || '-'}
                            </span>
                          </td>
                          <td className="p-2 text-right text-white mono">
                            {formatPrice(t.entry_price != null ? t.entry_price : t.price)}
                          </td>
                          <td className="p-2 text-right mono">
                            {isOpen && unrealized ? (
                              <span className="text-[#00D4FF]">${unrealized.currentPrice < 1 ? unrealized.currentPrice.toFixed(8) : unrealized.currentPrice.toFixed(2)}</span>
                            ) : t.entry_price != null ? (
                              <span className="text-[#A1A1AA]">{formatPrice(t.price)}</span>
                            ) : t.close_price ? (
                              <span className="text-[#A1A1AA]">{formatPrice(t.close_price)}</span>
                            ) : t.closed ? <span className="text-[#52525B]">Cerrado</span> : '-'}
                          </td>
                          <td className="p-2 text-right text-[#A1A1AA] mono">{t.amount || '-'}</td>
                          <td className={`p-2 text-right font-bold mono ${
                            t.profit_loss_usd != null ? (t.profit_loss_usd > 0 ? 'text-[#00FF88]' : t.profit_loss_usd < 0 ? 'text-[#FF3B30]' : 'text-[#52525B]') :
                            unrealized ? (unrealized.pnl >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]') : 'text-[#A1A1AA]'
                          }`}>
                            {t.profit_loss_usd != null ? (
                              `$${formatPnl(t.profit_loss_usd)}`
                            ) : unrealized ? `$${formatPnl(unrealized.pnl)}` : '-'}
                            {isOpen && unrealized && <span className="text-[10px] text-[#52525B] ml-1">(fees: ${unrealized.fees.toFixed(2)})</span>}
                          </td>
                          <td className="p-2">
                            <span className={`text-[10px] px-1 py-0.5 rounded ${t.paper_trading ? 'bg-[#00D4FF]/10 text-[#00D4FF]' : 'bg-[#FFD600]/10 text-[#FFD600]'}`}>
                              {t.paper_trading ? 'PAPER' : 'REAL'}
                            </span>
                          </td>
                          <td className="p-2">
                            {isOpen ? (
                              <button onClick={() => closeTrade(t)}
                                className="px-2 py-1 rounded text-[10px] font-bold bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20 hover:bg-[#FF3B30]/20 transition-all">
                                Cerrar
                              </button>
                            ) : t.bot_eliminado ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-[9px] px-1 py-0.5 rounded bg-[#52525B]/20 text-[#A1A1AA]">bot eliminado</span>
                                <button onClick={() => deleteBotHistory(t.bot_id)}
                                  title="Eliminar historial de este bot"
                                  className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20 hover:bg-[#FF3B30]/20 transition-all">
                                  Eliminar
                                </button>
                              </div>
                            ) : <span className="text-[10px] text-[#52525B]">{t.bot_id ? t.bot_id.slice(0, 10) : '-'}</span>}
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tab Trading Manual */}
        {tab === 'manual' && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white text-base">Operaciones Manuales</CardTitle></CardHeader>
            <CardContent>
              <EquityChart data={manualEquity} />
              {manualTrades.length === 0 ? (
                <p className="text-[#A1A1AA] text-center py-6">Sin operaciones manuales todavia</p>
              ) : (
                <div style={{maxHeight:'420px',overflowY:'auto',overflowX:'auto'}}
                     className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-[#27272A]">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="sticky top-0 bg-[#121212] z-10">
                      <tr className="border-b border-[#27272A] text-[#A1A1AA] text-xs">
                        <th className="text-left p-2">Fecha</th>
                        <th className="text-left p-2">Modo</th>
                        <th className="text-left p-2">Exchange</th>
                        <th className="text-left p-2">Par</th>
                        <th className="text-left p-2">Lado</th>
                        <th className="text-right p-2">Precio Entrada</th>
                        <th className="text-right p-2">Precio Cierre</th>
                        <th className="text-right p-2">Cantidad</th>
                        <th className="text-right p-2">P&L</th>
                        <th className="text-left p-2">Estado</th>
                        <th className="text-left p-2">Accion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualTrades.map((t, i) => {
                        const isOpen = !t.closed && t.profit_loss_usd == null;
                        return (
                        <tr key={t.trade_id || i} className={`border-b border-[#27272A]/30 hover:bg-[#1E1E1E] ${isOpen ? 'bg-[#1E1E1E]/50' : ''}`}>
                          <td className="p-2 text-[#A1A1AA] text-xs">{formatDate(t.created_at)}</td>
                          <td className="p-2">
                            <span className={`text-[10px] px-1 py-0.5 rounded font-bold ${t.paper_trading ? 'bg-[#00D4FF]/10 text-[#00D4FF]' : 'bg-[#FFD600]/10 text-[#FFD600]'}`}>
                              {t.paper_trading ? 'PAPER' : 'REAL'}
                            </span>
                          </td>
                          <td className="p-2 text-white uppercase text-xs">{t.exchange_id || '-'}</td>
                          <td className="p-2 text-white mono">{t.symbol}</td>
                          <td className="p-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${t.side === 'buy' ? 'bg-[#00FF88]/15 text-[#00FF88]' : 'bg-[#FF3B30]/15 text-[#FF3B30]'}`}>
                              {t.side?.toUpperCase() || '-'}
                            </span>
                          </td>
                          <td className="p-2 text-right text-white mono">{t.price ? `$${t.price < 1 ? t.price.toFixed(8) : t.price.toFixed(2)}` : '-'}</td>
                          <td className="p-2 text-right mono">
                            {t.close_price ? <span className="text-[#A1A1AA]">${t.close_price < 1 ? t.close_price.toFixed(8) : t.close_price.toFixed(2)}</span>
                             : isOpen ? <span className="text-[#52525B] text-xs">Abierta</span> : '-'}
                          </td>
                          <td className="p-2 text-right text-[#A1A1AA] mono">{t.amount || '-'}</td>
                          <td className={`p-2 text-right font-bold mono ${t.profit_loss_usd != null ? (t.profit_loss_usd > 0 ? 'text-[#00FF88]' : t.profit_loss_usd < 0 ? 'text-[#FF3B30]' : 'text-[#52525B]') : 'text-[#A1A1AA]'}`}>
                            {t.profit_loss_usd != null
                              ? `$${formatPnl(t.profit_loss_usd)}`
                              : isOpen
                                ? <span className="text-xs text-[#52525B]">Pendiente</span>
                                : <span className="text-xs text-[#52525B]" title="Precio de entrada no disponible — consulta tu exchange">Sin datos</span>
                            }
                          </td>
                          <td className="p-2">
                            <span className={`text-[10px] px-1 py-0.5 rounded ${t.closed ? 'bg-[#27272A] text-[#52525B]' : 'bg-[#00FF88]/10 text-[#00FF88]'}`}>
                              {t.closed ? 'CERRADA' : t.status || 'ABIERTA'}
                            </span>
                          </td>
                          <td className="p-2">
                            {isOpen ? (
                              <button onClick={() => closeTrade(t)}
                                className="px-2 py-1 rounded text-[10px] font-bold bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20 hover:bg-[#FF3B30]/20 transition-all">
                                Cerrar
                              </button>
                            ) : t.copy_of_trader ? (
                              <span className="text-[10px] px-1 py-0.5 rounded bg-purple-500/10 text-purple-400">COPY</span>
                            ) : <span className="text-[10px] text-[#52525B]">—</span>}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {/* Tab Arbitraje */}
        {tab === 'arbitrage' && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white text-base">Operaciones de Arbitraje</CardTitle></CardHeader>
            <CardContent>
              <EquityChart data={arbEquity} />
              {arbTrades.length === 0 ? (
                <p className="text-[#A1A1AA] text-center py-6">Sin operaciones de arbitraje todavia</p>
              ) : (
                <div style={{maxHeight:'420px',overflowY:'auto',overflowX:'auto'}}
                     className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-[#27272A]">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="sticky top-0 bg-[#121212] z-10">
                      <tr className="border-b border-[#27272A] text-[#A1A1AA] text-xs">
                        <th className="text-left p-2">Fecha</th>
                        <th className="text-left p-2">Par</th>
                        <th className="text-left p-2">Tipo</th>
                        <th className="text-left p-2">Comprador</th>
                        <th className="text-left p-2">Vendedor</th>
                        <th className="text-right p-2">Diferencia %</th>
                        <th className="text-right p-2">Ganancia</th>
                        <th className="text-left p-2">Modo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {arbTrades.map((t, i) => (
                        <tr key={t.arb_id || i} className="border-b border-[#27272A]/30 hover:bg-[#1E1E1E]">
                          <td className="p-2 text-[#A1A1AA] text-xs">{formatDate(t.timestamp)}</td>
                          <td className="p-2 text-white mono">{t.symbol || '-'}</td>
                          <td className="p-2">
                            <span className="text-[10px] px-1 py-0.5 rounded bg-[#7C3AED]/15 text-[#7C3AED]">
                              {t.tipo === 'futures_vs_futures' ? 'F-F' : t.tipo === 'spot_vs_futures' ? 'S-F' : t.tipo || '-'}
                            </span>
                          </td>
                          <td className="p-2 text-[#A1A1AA] text-xs uppercase">{t.buy_exchange || '-'}</td>
                          <td className="p-2 text-[#A1A1AA] text-xs uppercase">{t.sell_exchange || '-'}</td>
                          <td className="p-2 text-right mono text-[#00D4FF]">
                            {t.profit_percentage != null ? `${Number(t.profit_percentage).toFixed(3)}%` : '-'}
                          </td>
                          <td className={`p-2 text-right font-bold mono ${(t.ganancia_usd || 0) >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                            {t.ganancia_usd != null ? `$${Number(t.ganancia_usd).toFixed(4)}` : '-'}
                          </td>
                          <td className="p-2">
                            <span className={`text-[10px] px-1 py-0.5 rounded ${t.paper_trading ? 'bg-[#00D4FF]/10 text-[#00D4FF]' : 'bg-[#FFD600]/10 text-[#FFD600]'}`}>
                              {t.paper_trading ? 'PAPER' : 'REAL'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

      </div>
    </Layout>
  );
};

export default History;
